import React, { useState } from "react";
import { FlaskConical, Loader2, Image as ImageIcon, Film, CheckCircle2, AlertTriangle, Clapperboard } from "lucide-react";
import { CharacterPicker } from "@/components/CharacterPicker";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/** خانة اختبار القوانين: صورة فعلية مع تقرير الفحص/إعادة التوليد، أو خطة فيديو بلا تصيير */
export const RulesTestBox = () => {
  const [text, setText] = useState("");
  const [product, setProduct] = useState("");
  const [kind, setKind] = useState("image");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [gender, setGender] = useState("male");
  const [pick, setPick] = useState({});
  const [vid, setVid] = useState(null);
  const charOpts = { gender, voice: pick.voice || "", voice_style: pick.style || "", anim_gender: pick.animGender || "" };
  const run = async () => {
    setBusy(true); setRes(null); setVid(null);
    try {
      const { data } = await api.post("/admin/rawaj-rules/test", { text, kind, product_name: product, ...charOpts });
      if (data.kind === "video") { setRes(data); toast.success("اكتملت خطة الفيديو"); setBusy(false); return; }
      toast.info("بدأ توليد الصورة واختبار القوانين — قد يستغرق حتى 4 دقائق مع إعادة التوليد");
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/admin/rawaj-rules/test/${data.job_id}`);
          if (st.status === "done") { setRes(st.result); toast.success("اكتمل اختبار الصورة"); setBusy(false); return; }
          if (st.status === "failed") { toast.error(st.error || "فشل الاختبار"); setBusy(false); return; }
        } catch { /* keep polling */ }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setBusy(false); }
  };
  const render = async () => {
    setVid({ status: "processing", progress: 0 });
    try {
      const { data } = await api.post("/admin/rawaj-rules/test/render", { text, product_name: product, ...charOpts });
      toast.info("بدأ توليد الفيديو بخط إنتاج العميل نفسه — عادةً 4–8 دقائق");
      const end = Date.now() + 15 * 60 * 1000;
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/content/video/${data.content_id}/status`);
          if (st.status === "completed") { setVid(st); toast.success("اكتمل الفيديو التجريبي"); return; }
          if (st.status === "failed") { setVid(st); toast.error(st.error || "فشل توليد الفيديو"); return; }
          setVid(st);
        } catch { /* keep polling */ }
        if (Date.now() < end) setTimeout(poll, 5000); else setVid({ status: "failed", error: "انتهت مهلة الانتظار" });
      };
      setTimeout(poll, 5000);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setVid(null); }
  };
  const a = res?.audit || {};
  return (
    <div className="rounded-xl border border-accent/40 bg-accent/5 p-4 space-y-3" data-testid="rules-test-box">
      <div className="flex items-center gap-2 font-bold text-sm"><FlaskConical className="w-4 h-4 text-accent" /> اختبار القوانين على توليد حقيقي</div>
      <p className="text-xs text-muted-foreground">اكتب وصف منتج أو مشهد قصير؛ تُطبَّق القوانين المحفوظة كاملة. الصورة توليد فعلي مع تقرير الفحص وإعادة التوليد، والفيديو خطة المخرج (اللقطات والجمل والمشاهد) أولًا، ثم زر لتوليده فعليًا كما يفعل العميل.</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="مثال: خصم 50٪ على الآيس لاتيه في مقهى دندنة هذا الأسبوع" className="bg-secondary border-border sm:col-span-2 text-sm" data-testid="rules-test-text" />
        <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="اسم المنتج (اختياري)" className="bg-secondary border-border" data-optional="true" data-testid="rules-test-product" />
      </div>
      {kind === "video" && <CharacterPicker compact value={gender} onChange={setGender} pick={pick} onPick={setPick} testId="rules-test-char" productName={product} hint="" />}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-full border border-border bg-background/60 p-0.5 text-xs" data-testid="rules-test-kind">
          {[["image", "صورة", ImageIcon], ["video", "خطة فيديو", Film]].map(([k, l, I]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`rules-test-kind-${k}`}
              className={`px-3 py-1 rounded-full font-bold flex items-center gap-1 transition-colors ${kind === k ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}><I className="w-3.5 h-3.5" /> {l}</button>
          ))}
        </div>
        <Button onClick={run} disabled={busy || text.trim().length < 5} className="bg-rose-600 hover:bg-rose-700 text-white gap-1 h-8 text-xs font-bold" data-testid="rules-test-run">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} {busy ? "جارٍ الاختبار…" : "اختبر التوليد"}
        </Button>
      </div>
      {res?.kind === "image" && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="rules-test-image-result">
          <img src={res.image} alt="نتيجة الاختبار" className="rounded-xl border border-border w-full" data-testid="rules-test-image" />
          <div className="text-xs space-y-2">
            <div className={`rounded-lg border p-2 ${a.issues_final?.length ? "border-rose-500/50 bg-rose-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="rules-test-audit">
              <div className="font-bold flex items-center gap-1">{a.issues_final?.length ? <AlertTriangle className="w-3.5 h-3.5 text-rose-400" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                {a.audited ? (a.issues_final?.length ? "بقيت مخالفات بعد إعادة التوليد" : "اجتازت فحص القوانين") : "الفحص غير مفعّل"} · إعادة التوليد: {a.retries || 0}</div>
              {a.issues_initial?.length > 0 && <div className="mt-1"><b>مخالفات الفحص الأول:</b><ul className="list-disc ps-4">{a.issues_initial.map((x, i) => <li key={i} dir="ltr" className="text-start">{x}</li>)}</ul></div>}
              {a.issues_final?.length > 0 && <div className="mt-1 text-rose-300"><b>المتبقي:</b><ul className="list-disc ps-4">{a.issues_final.map((x, i) => <li key={i} dir="ltr" className="text-start">{x}</li>)}</ul></div>}
            </div>
            <div><b>المشهد المخطط:</b> <span dir="ltr" className="block text-muted-foreground">{res.scene}</span></div>
          </div>
        </div>
      )}
      {res?.kind === "video" && (
        <div className="text-xs space-y-2" data-testid="rules-test-video-result">
          <div><b>الفكرة:</b> {res.creative_idea} · <b>الصوت:</b> {res.voice}{res.landmark && <> · <b>معلم الخلفية:</b> <span dir="ltr" data-testid="rules-test-landmark">{res.landmark}</span></>}</div>
          {(res.shots || []).map((s, i) => (
            <div key={i} className="rounded-lg border border-border p-2 bg-background/40">
              <div className="font-bold">لقطة {i + 1} — {s.subject === "product" ? "المنتج" : "شخص"}{s.caption ? ` · كابشن: ${s.caption}` : ""}</div>
              <div><b>التعليق:</b> {s.line} {s.tts && <span className="text-muted-foreground">({s.tts})</span>}</div>
              <div dir="ltr" className="text-start text-muted-foreground mt-1">{s.scene}</div>
            </div>
          ))}
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-3 space-y-2" data-testid="rules-test-render-box">
            <p className="text-muted-foreground">محاكاة العميل: يولّد الفيديو فعليًا بالخطة أعلاه عبر خط الإنتاج نفسه (المخرج، الصوت المختار، الفحص وإعادة التوليد، الشعار) — بلا خصم رصيد. استخدمه لتقييم الالتزام بالسكربت والشماغ والنطق.</p>
            <Button onClick={render} disabled={vid?.status === "processing"} className="bg-rose-600 hover:bg-rose-700 text-white gap-1 h-8 text-xs font-bold" data-testid="rules-test-render">
              {vid?.status === "processing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clapperboard className="w-3.5 h-3.5" />}
              {vid?.status === "processing" ? `جارٍ التوليد… ${Math.round(vid.progress || 0)}٪ (${vid.stage || "scene"})` : "ولّد الفيديو فعليًا واختبره"}
            </Button>
            {vid?.status === "processing" && <div className="h-1.5 rounded-full bg-background/60 overflow-hidden"><div className="h-full bg-rose-500 transition-[width]" style={{ width: `${Math.max(3, vid.progress || 0)}%` }} /></div>}
            {vid?.status === "failed" && <div className="text-rose-300 flex items-center gap-1" data-testid="rules-test-render-error"><AlertTriangle className="w-3.5 h-3.5" /> {vid.error || "فشل التوليد"}</div>}
            {vid?.status === "completed" && vid.url && (
              <div className="space-y-1" data-testid="rules-test-render-result">
                <video src={vid.url} controls playsInline className="rounded-xl border border-border w-full max-w-[280px] mx-auto" data-testid="rules-test-video" />
                <div className="text-muted-foreground text-center">الصوت: {vid.voice_name || res.voice}{vid.voice_style ? ` · ${vid.voice_style}` : ""}</div>
                {vid.voice_warning && <div className="text-amber-300 flex items-start gap-1 text-xs" data-testid="rules-test-voice-warning"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {vid.voice_warning}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
